import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, FileText, Save, RotateCcw, Receipt, AlertTriangle, Clock, Calendar, Wallet, FileSignature, Truck, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_LH = {
  inherit_from_default: false,
  logo_position: "right",
  logo_size: "md",
  show_slogan: true,
  slogan_text: "seit 2011",
  slogan_color: "#C28535",
  show_company_info: true,
  company_info_alignment: "right",
  company_info_fields: ["legal_name", "address", "contact", "ids"],
  header_separator: "line",
  accent_color: "#C28535",
  show_legal_name: true,
  show_full_address: true,
  show_contact: false,
  show_uid_fn: true,
  show_bank: true,
  custom_footer_text: "",
  footer_alignment: "center",
  footer_separator: true,
};

const DOC_TYPES = [
  { key: "default", label: "Allgemein", Icon: FileText, hint: "Wirkt als Vorlage für alle Dokumente" },
  { key: "rechnung", label: "Rechnung", Icon: Receipt },
  { key: "mahnung", label: "Mahnung", Icon: AlertTriangle },
  { key: "lenkprotokoll", label: "Lenkprotokoll", Icon: Clock },
  { key: "zeiterfassung", label: "Zeiterfassung", Icon: Calendar },
  { key: "lohn", label: "Lohn & Gehalt", Icon: Wallet },
  { key: "vertrag", label: "Vertrag", Icon: FileSignature },
  { key: "kfz_uebergabe", label: "KFZ-Übergabe", Icon: Truck },
  { key: "bericht", label: "Bericht", Icon: BarChart3 },
];

const Lbl = ({ children }) => (
  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1.5">{children}</label>
);

const Section = ({ title, children }) => (
  <div className="bg-zinc-950 border border-zinc-800 rounded p-4 space-y-3">
    <div className="text-xs font-semibold text-zinc-300 uppercase tracking-wider pb-2 border-b border-zinc-800">{title}</div>
    {children}
  </div>
);

const Radio = ({ value, current, label, onChange, testid }) => (
  <button type="button" onClick={() => onChange(value)} data-testid={testid}
    className={`px-3 py-1.5 text-xs rounded border transition ${current === value ? "bg-amber-600/15 border-amber-500 text-amber-300" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
    {label}
  </button>
);

const Toggle = ({ checked, onChange, label, testid, disabled }) => (
  <label className={`flex items-center gap-2 ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} text-sm text-zinc-300`}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)}
      data-testid={testid} className="accent-amber-500" />
    <span>{label}</span>
  </label>
);

export default function LetterheadEditor({ profile, onSaved, disabled }) {
  const [activeDocType, setActiveDocType] = useState("default");
  const [letterheads, setLetterheads] = useState(() => {
    const lhMap = profile?.letterheads || {};
    const out = {};
    DOC_TYPES.forEach(({ key }) => {
      out[key] = { ...DEFAULT_LH, ...(lhMap[key] || {}) };
    });
    if (!profile?.letterheads?.default && profile?.letterhead) {
      // Legacy migration
      out.default = { ...DEFAULT_LH, ...profile.letterhead };
    }
    return out;
  });
  const [primaryColor, setPrimaryColor] = useState(profile?.primary_color || "#C28535");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef();

  const lh = letterheads[activeDocType] || DEFAULT_LH;
  const isInheriting = activeDocType !== "default" && lh.inherit_from_default;
  const effectiveLh = isInheriting ? letterheads.default : lh;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refreshPreview, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocType, letterheads, primaryColor]);

  async function refreshPreview() {
    setPreviewing(true);
    try {
      const { data } = await api.post("/profile/letterhead/preview-html",
        { letterhead: effectiveLh, primary_color: primaryColor, doc_type: activeDocType },
        { responseType: "text" });
      setPreviewHtml(typeof data === "string" ? data : "");
    } catch {
      toast.error("Vorschau konnte nicht geladen werden");
    } finally {
      setPreviewing(false);
    }
  }

  async function downloadPdf() {
    try {
      const res = await api.post("/profile/letterhead/preview",
        { letterhead: effectiveLh, primary_color: primaryColor, doc_type: activeDocType },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Briefkopf-${activeDocType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF-Download fehlgeschlagen");
    }
  }

  async function save() {
    setBusy(true);
    try {
      const updated = {
        ...profile,
        letterhead: letterheads.default,        // legacy compat
        letterheads,
        primary_color: primaryColor,
      };
      const { data } = await api.put("/profile/company", updated);
      toast.success("Briefkopf gespeichert");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  function resetCurrent() {
    setLetterheads((p) => ({
      ...p,
      [activeDocType]: { ...DEFAULT_LH, inherit_from_default: activeDocType !== "default" },
    }));
    if (activeDocType === "default") setPrimaryColor("#C28535");
  }

  function set(k, v) {
    setLetterheads((p) => ({ ...p, [activeDocType]: { ...p[activeDocType], [k]: v } }));
  }
  function toggleField(f) {
    setLetterheads((p) => {
      const cur = new Set(p[activeDocType].company_info_fields || []);
      if (cur.has(f)) cur.delete(f); else cur.add(f);
      return { ...p, [activeDocType]: { ...p[activeDocType], company_info_fields: Array.from(cur) } };
    });
  }

  return (
    <div className="space-y-4" data-testid="letterhead-editor">
      {/* Document-type subtabs */}
      <div className="bg-zinc-950 border border-zinc-800 rounded p-2 flex flex-wrap gap-1">
        {DOC_TYPES.map(({ key, label, Icon }) => (
          <button key={key} type="button" onClick={() => setActiveDocType(key)}
            data-testid={`lh-tab-${key}`}
            className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition ${activeDocType === key ? "bg-amber-600/20 text-amber-300 border border-amber-500/40" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* LEFT: Controls */}
        <div className="lg:col-span-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-zinc-50">
                {DOC_TYPES.find((d) => d.key === activeDocType)?.label} · Briefkopf
              </h3>
              {DOC_TYPES.find((d) => d.key === activeDocType)?.hint && (
                <p className="text-[10px] text-zinc-500 mt-0.5">{DOC_TYPES.find((d) => d.key === activeDocType).hint}</p>
              )}
            </div>
            <button onClick={resetCurrent} disabled={disabled} data-testid="lh-reset"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded">
              <RotateCcw className="w-3 h-3" /> Zurücksetzen
            </button>
          </div>

          {/* Inherit-from-default checkbox (only for non-default tabs) */}
          {activeDocType !== "default" && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3">
              <Toggle checked={lh.inherit_from_default}
                onChange={(v) => set("inherit_from_default", v)}
                label="Vom Allgemein-Briefkopf erben (deaktivieren um eigene Einstellungen zu nutzen)"
                testid="lh-inherit" />
            </div>
          )}

          {/* All controls below — disabled if inheriting */}
          <fieldset disabled={isInheriting} className={isInheriting ? "opacity-50 pointer-events-none" : ""}>
            <Section title="Header">
              <div>
                <Lbl>Logo-Position</Lbl>
                <div className="flex flex-wrap gap-1.5">
                  <Radio value="left" current={lh.logo_position} label="Links" onChange={(v) => set("logo_position", v)} testid="lh-pos-left" />
                  <Radio value="center" current={lh.logo_position} label="Mitte" onChange={(v) => set("logo_position", v)} testid="lh-pos-center" />
                  <Radio value="right" current={lh.logo_position} label="Rechts" onChange={(v) => set("logo_position", v)} testid="lh-pos-right" />
                  <Radio value="none" current={lh.logo_position} label="Kein Logo" onChange={(v) => set("logo_position", v)} testid="lh-pos-none" />
                </div>
              </div>

              <div>
                <Lbl>Logo-Größe</Lbl>
                <div className="flex gap-1.5">
                  <Radio value="sm" current={lh.logo_size} label="Klein (16mm)" onChange={(v) => set("logo_size", v)} testid="lh-size-sm" />
                  <Radio value="md" current={lh.logo_size} label="Mittel (22mm)" onChange={(v) => set("logo_size", v)} testid="lh-size-md" />
                  <Radio value="lg" current={lh.logo_size} label="Groß (30mm)" onChange={(v) => set("logo_size", v)} testid="lh-size-lg" />
                </div>
              </div>

              <Toggle checked={lh.show_slogan} onChange={(v) => set("show_slogan", v)} label="Slogan unter Logo anzeigen" testid="lh-show-slogan" />
              {lh.show_slogan && (
                <div className="grid grid-cols-2 gap-2 pl-6">
                  <input type="text" value={lh.slogan_text || ""} onChange={(e) => set("slogan_text", e.target.value)}
                    placeholder="z. B. seit 2011" data-testid="lh-slogan-text"
                    className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100" />
                  <div className="flex items-center gap-2">
                    <input type="color" value={lh.slogan_color || "#C28535"} onChange={(e) => set("slogan_color", e.target.value)}
                      data-testid="lh-slogan-color" className="w-9 h-7 rounded border border-zinc-700 bg-zinc-900" />
                    <span className="text-xs text-zinc-500 font-mono">{lh.slogan_color}</span>
                  </div>
                </div>
              )}

              <Toggle checked={lh.show_company_info} onChange={(v) => set("show_company_info", v)} label="Firmen-Info-Block anzeigen" testid="lh-show-info" />
              {lh.show_company_info && (
                <div className="pl-6 space-y-2">
                  <div>
                    <Lbl>Ausrichtung</Lbl>
                    <div className="flex gap-1.5">
                      <Radio value="left" current={lh.company_info_alignment} label="Links" onChange={(v) => set("company_info_alignment", v)} testid="lh-info-align-left" />
                      <Radio value="right" current={lh.company_info_alignment} label="Rechts" onChange={(v) => set("company_info_alignment", v)} testid="lh-info-align-right" />
                    </div>
                  </div>
                  <div>
                    <Lbl>Anzuzeigende Felder</Lbl>
                    <div className="grid grid-cols-2 gap-y-1">
                      {[
                        ["legal_name", "Firmenname"],
                        ["address", "Adresse"],
                        ["contact", "Tel/Email"],
                        ["ids", "UID/FN"],
                      ].map(([k, l]) => (
                        <Toggle key={k} checked={(lh.company_info_fields || []).includes(k)}
                          onChange={() => toggleField(k)} label={l} testid={`lh-info-field-${k}`} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Lbl>Trennlinie</Lbl>
                <div className="flex gap-1.5">
                  <Radio value="line" current={lh.header_separator} label="Linie" onChange={(v) => set("header_separator", v)} testid="lh-sep-line" />
                  <Radio value="double" current={lh.header_separator} label="Doppellinie" onChange={(v) => set("header_separator", v)} testid="lh-sep-double" />
                  <Radio value="none" current={lh.header_separator} label="Keine" onChange={(v) => set("header_separator", v)} testid="lh-sep-none" />
                </div>
              </div>
            </Section>

            <Section title="Akzentfarbe (Tabellenkopf, Linien, Hervorhebungen)">
              {activeDocType === "default" && (
                <div className="flex items-center gap-3">
                  <Lbl>Plattform-Hauptfarbe</Lbl>
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)}
                    data-testid="lh-primary-color" className="w-10 h-8 rounded border border-zinc-700 bg-zinc-900 cursor-pointer" />
                  <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)}
                    data-testid="lh-primary-color-hex"
                    className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-sm font-mono text-zinc-100" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <Lbl>Dokument-Akzent</Lbl>
                <input type="color" value={lh.accent_color || "#C28535"} onChange={(e) => set("accent_color", e.target.value)}
                  data-testid="lh-accent-color" className="w-10 h-8 rounded border border-zinc-700 bg-zinc-900 cursor-pointer" />
                <input type="text" value={lh.accent_color || "#C28535"} onChange={(e) => set("accent_color", e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-sm font-mono text-zinc-100" />
                <button type="button" onClick={() => set("accent_color", "#C28535")}
                  className="text-xs text-amber-400 hover:text-amber-300 whitespace-nowrap">Munja-Amber</button>
              </div>
            </Section>

            <Section title="Footer">
              <Toggle checked={lh.show_legal_name} onChange={(v) => set("show_legal_name", v)} label="Firmenname (rechtlich)" testid="lh-foot-legal" />
              <Toggle checked={lh.show_full_address} onChange={(v) => set("show_full_address", v)} label="Vollständige Adresse" testid="lh-foot-addr" />
              <Toggle checked={lh.show_contact} onChange={(v) => set("show_contact", v)} label="Tel / Email" testid="lh-foot-contact" />
              <Toggle checked={lh.show_uid_fn} onChange={(v) => set("show_uid_fn", v)} label="UID / FN" testid="lh-foot-ids" />
              <Toggle checked={lh.show_bank} onChange={(v) => set("show_bank", v)} label="Bankverbindung (IBAN/BIC)" testid="lh-foot-bank" />
              <div>
                <Lbl>Eigener Zusatztext</Lbl>
                <textarea value={lh.custom_footer_text || ""} onChange={(e) => set("custom_footer_text", e.target.value)}
                  data-testid="lh-foot-custom" rows={2} placeholder="z. B. Werbeslogan, Notiz, GTC-Hinweis…"
                  className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100" />
              </div>
              <div>
                <Lbl>Ausrichtung</Lbl>
                <div className="flex gap-1.5">
                  <Radio value="left" current={lh.footer_alignment} label="Links" onChange={(v) => set("footer_alignment", v)} testid="lh-foot-align-left" />
                  <Radio value="center" current={lh.footer_alignment} label="Mitte" onChange={(v) => set("footer_alignment", v)} testid="lh-foot-align-center" />
                  <Radio value="right" current={lh.footer_alignment} label="Rechts" onChange={(v) => set("footer_alignment", v)} testid="lh-foot-align-right" />
                </div>
              </div>
              <Toggle checked={lh.footer_separator} onChange={(v) => set("footer_separator", v)} label="Trennlinie über Footer" testid="lh-foot-sep" />
            </Section>
          </fieldset>

          <div className="sticky bottom-4 bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-center justify-end gap-2">
            <button onClick={save} disabled={busy || disabled} data-testid="lh-save"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Alle Briefköpfe speichern
            </button>
          </div>
        </div>

        {/* RIGHT: Live A4 Preview */}
        <div className="lg:col-span-7 lg:sticky lg:top-4 lg:self-start">
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3 px-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-50">
                <FileText className="w-4 h-4 text-amber-500" />
                Live-Vorschau · {DOC_TYPES.find((d) => d.key === activeDocType)?.label}
                {isInheriting && <span className="text-[10px] text-amber-400 ml-1">(erbt von Allgemein)</span>}
              </div>
              <div className="flex items-center gap-2">
                {previewing && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
                <button onClick={downloadPdf} data-testid="lh-download-pdf"
                  className="text-[11px] text-amber-400 hover:text-amber-300 underline-offset-2 hover:underline">
                  PDF herunterladen
                </button>
              </div>
            </div>
            <div className="bg-white rounded shadow-2xl overflow-hidden" style={{ aspectRatio: "1 / 1.414" }}>
              {previewHtml ? (
                <iframe srcDoc={previewHtml} title="Briefkopf Vorschau" data-testid="lh-preview-iframe"
                  sandbox="allow-same-origin" className="w-full h-full border-0" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              )}
            </div>
            <div className="mt-2 text-[10px] text-zinc-500 text-center px-2">
              A4-Format · 18mm × 15mm Ränder · Vorschau zeigt typisches Beispiel des gewählten Dokumenttyps
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
